import { PipelineEntityId, PipelineEntityType, PipelineStageId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { trpc } from "components/rpc/trpc";
import { ActionBarComponent } from "components/shared/action-bar-component";
import { Select } from "components/ui/select";
import React from "react";
import { useFeature } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";

type Pipeline = inferProcedureOutput<AppRouter["pipeline"]["list"]>[number];
type EntityMembership = inferProcedureOutput<
    AppRouter["pipeline"]["entityStages"]
>[number];

interface PipelineStageActionBarsProps {
    entityType: PipelineEntityType;
    entityId: PipelineEntityId;
}

export const PipelineStageActionBars: React.FC<PipelineStageActionBarsProps> = props => {
    const { entityType, entityId } = props;
    const enabled = useFeature("referralPipelines");
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();

    const pipelinesQuery = useQuery({
        ...trpcClient.pipeline.list.queryOptions(),
        enabled,
    });
    const membershipsOptions = trpcClient.pipeline.entityStages.queryOptions({
        entityType,
        entityId,
    });
    const membershipsQuery = useQuery({ ...membershipsOptions, enabled });

    const onSuccess = () => {
        queryClient.invalidateQueries({ queryKey: membershipsOptions.queryKey });
    };

    const addCard = useMutation({
        ...trpcClient.pipeline.addCard.mutationOptions(),
        onSuccess,
    });
    const moveCard = useMutation({
        ...trpcClient.pipeline.moveCard.mutationOptions(),
        onSuccess,
    });
    const removeCard = useMutation({
        ...trpcClient.pipeline.removeCard.mutationOptions(),
        onSuccess,
    });

    if (!enabled) {
        return null;
    }

    const pipelines = pipelinesQuery.data ?? [];
    const memberships = membershipsQuery.data ?? [];

    const setStage = (membership: EntityMembership | undefined, value: string) => {
        if (value === "") {
            if (membership) {
                handleEvent("referral-pipeline-remove-card", {
                    source: "referral-source-page",
                });
                removeCard.mutate({ membershipId: membership.id });
            }
            return;
        }
        const stageId = value as PipelineStageId;
        if (membership) {
            handleEvent("referral-pipeline-move-card", {
                source: "referral-source-page",
            });
            moveCard.mutate({ membershipId: membership.id, stageId });
        } else {
            handleEvent("referral-pipeline-add-card", {
                source: "referral-source-page",
            });
            addCard.mutate({ stageId, entityType, entityId });
        }
    };

    return (
        <>
            {pipelines.map(pipeline => (
                <PipelineStageActionBar
                    key={pipeline.id}
                    pipeline={pipeline}
                    membership={memberships.find(m => m.pipelineId === pipeline.id)}
                    onChange={setStage}
                />
            ))}
        </>
    );
};

interface PipelineStageActionBarProps {
    pipeline: Pipeline;
    membership: EntityMembership | undefined;
    onChange: (membership: EntityMembership | undefined, value: string) => void;
}

const PipelineStageActionBar: React.FC<PipelineStageActionBarProps> = props => {
    const { pipeline, membership, onChange } = props;
    const currentStage = membership
        ? pipeline.stages.find(stage => stage.id === membership.pipelineStageId)
        : undefined;

    return (
        <ActionBarComponent label={pipeline.name} value={currentStage?.name ?? "None"}>
            <div className="p-4">
                <Select
                    label={pipeline.name}
                    value={membership?.pipelineStageId ?? ""}
                    onChange={e => onChange(membership, e.target.value)}>
                    <option value="">No stage</option>
                    {pipeline.stages.map(stage => (
                        <option key={stage.id} value={stage.id}>
                            {stage.name}
                        </option>
                    ))}
                </Select>
            </div>
        </ActionBarComponent>
    );
};
